import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

// Types for the API responses
export interface SimpleStaffProfileResponse {
  hireDate?: string;
  performance: number;
  currentWorkload: number;
  averageRating: number;
  taskCompletionRate: number;
  profilePicture: string;
  totalSkills: number;
  yearsExperience: number;
  startDate?: string;
}

export interface FullStaffProfileResponse {
  // Basic Information
  // Experience & Tenure
  experienceMetrics: {
    totalWorkExperienceMonths: number;
    currentTenureMonths: number;
    industryExperienceMonths: number;
    yearsExperience: number;
  };

  // Skills & Competencies
  skills: {
    name: string;
    level: string;
    yearsExperience: number;
    monthsExperience: number;
    lastUsed?: string;
    source: string;
    confidence: number;
    verified: boolean;
    verifiedBy?: string;
    verifiedAt?: string;
    performanceRating: number;
    notes?: string;
    trainingCompleted: string[];
  }[];

  skillsSummary: {
    totalSkills: number;
    verifiedSkills: number;
    skillsByLevel: {
      novice: number;
      intermediate: number;
      advanced: number;
      expert: number;
    };
    topSkills: {
      name: string;
      level: string;
      performanceRating: number;
      confidence: number;
      yearsExperience: number;
    }[];
  };

  // Work Experience
  workExperience: {
    companyName: string;
    position: string;
    industry: string;
    startDate: string;
    endDate?: string;
    durationMonths: number;
    type: string;
    responsibilities: string[];
    skillsGained: string[];
    achievements: string[];
    verified: boolean;
  }[];

  // Education & Training
  education: {
    institution: string;
    degree: string;
    field: string;
    graduationYear?: number;
    gpa?: number;
    honors?: string[];
  }[];

  training: {
    name: string;
    provider: string;
    completedDate: string;
    expirationDate?: string;
    certificateUrl?: string;
    skillsLearned: string[];
  }[];

  certifications: string[];

  // Performance & Analytics
  performanceMetrics: {
    tasksCompleted: number;
    tasksOnTime: number;
    averageTaskRating: number;
    customerFeedbackScore: number;
    peerRatingAverage: number;
    improvementAreas: string[];
    strengths: string[];
    lastPerformanceReview?: string;
    nextPerformanceReview?: string;
  };

  // Current Status
  currentStatus: {
    workload: number;
    averageRating: number;
    taskCompletionRate: number;
    availability?: any;
  };

  // Development & Goals
  developmentGoals: {
    skillName: string;
    targetLevel: string;
    targetDate: string;
    currentProgress: number;
    status: string;
    milestones: {
      description: string;
      completed: boolean;
      completedDate?: string;
    }[];
    assignedBy?: string;
  }[];

  performanceHistory: {
    skillName: string;
    assessmentDate: string;
    assessorType: string;
    rating: number;
    notes?: string;
    improvementPlan?: string;
  }[];

  // Location & Contact
  location?: any;
  preferences?: any;
}

// Employee without quality role response
export interface EmployeeWithoutQualityRoleResponse {
  employees: Array<{
    id: string;
    name: string;
    surname: string;
    email: string;
    currentRole?: string;
  }>;
  total: number;
}

// Department types
export interface CreateDepartmentRequest {
  name: string;
  requiredSkills?: string[];
  optionalSkills?: string[];
  skillWeights?: Record<string, number>;
  metadata?: {
    phpId?: string | null;
    phpCreatedAt?: string;
    shortCode?: string;
    [key: string]: any;
  };
}

export interface CreateDepartmentResponse {
  success: boolean;
  departmentId: string;
  message: string;
}

export interface UpdateDepartmentRequest {
  name?: string;
  requiredSkills?: string[];
  optionalSkills?: string[];
  skillWeights?: Record<string, number>;
  metadata?: {
    phpId?: string | null;
    phpCreatedAt?: string;
    shortCode?: string;
    [key: string]: any;
  };
}

export interface UpdateDepartmentResponse {
  success: boolean;
  message: string;
}

export interface RemoveDepartmentResponse {
  success: boolean;
  message: string;
}

// Team types
export interface CreateTeamRequest {
  name: string;
  metadata?: {
    phpId?: string | null;
    phpCreatedAt?: string;
    shortCode?: string;
    [key: string]: any;
  };
}

export interface CreateTeamResponse {
  success: boolean;
  teamId: string;
  message: string;
}

export interface UpdateTeamRequest {
  name?: string;
  metadata?: {
    phpId?: string | null;
    phpCreatedAt?: string;
    shortCode?: string;
    [key: string]: any;
  };
}

export interface UpdateTeamResponse {
  success: boolean;
  message: string;
}

export interface RemoveTeamResponse {
  success: boolean;
  message: string;
}

export interface CreateRoleRequest {
  name: string;
  description?: string;
  isCustom?: boolean;
  metadata?: {
    phpId?: string | null;
    phpCreatedAt?: string;
    phpUpdatedAt?: string;
    shortCode?: string;
    venueId?: string;
    roleType?: 'attached' | 'custom';
    syncVersion?: number;
    source?: string;
    [key: string]: any;
  };
}

export interface CreateRoleResponse {
  success: boolean;
  roleId: string;
  message: string;
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  metadata?: {
    phpId?: string | null;
    phpCreatedAt?: string;
    phpUpdatedAt?: string;
    shortCode?: string;
    venueId?: string;
    roleType?: 'attached' | 'custom';
    syncVersion?: number;
    source?: string;
    [key: string]: any;
  };
}


export interface UpdateRoleResponse {
  success: boolean;
  message: string;
}

export interface RemoveRoleResponse {
  success: boolean;
  message: string;
}

// ============================================================================
// PROJECTS TYPES (NEW)
// ============================================================================

export interface AppProject {
  _id: string;
  name: string;
  description?: string;
  businessId: string;
  clientId?: string;
  status: string;
  externalIds: {
    // @ts-ignore
    venueBoostProjectId?: string;
    [key: string]: string;
  };
  metadata: {
    status?: string;
    projectType?: string;
    lastSyncedAt?: Date;
    estimatedHours?: number;
    estimatedBudget?: number;
    startDate?: Date;
    endDate?: Date;
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      city?: string;
      state?: string;
      country?: string;
    };
    clientInfo?: {
      id: string;
      name: string;
      email?: string;
      phone?: string;
    };
    weatherDelays?: Array<{
      date: Date;
      hours: number;
      reason: string;
      weatherType: string;
      notes?: string;
      createdAt: Date;
    }>;
    totalWeatherDelayHours?: number;
    [key: string]: any;
  };
  isDeleted: boolean;
  deletedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetProjectsResponse {
  projects: AppProject[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================================
// CONSTRUCTION SITES TYPES (NEW)
// ============================================================================

export interface ConstructionSite {
  _id: string;
  name: string;
  description?: string;
  businessId: string;
  appProjectId?: string;
  status: string;
  type: string;
  externalIds: {
    // @ts-ignore
    venueBoostSiteId?: string;
    [key: string]: string;
  };
  location: {
    latitude?: number;
    longitude?: number;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  metadata: {
    status?: string;
    siteType?: string;
    lastSyncedAt?: Date;
    startDate?: Date;
    endDate?: Date;
    noOfWorkers?: number;
    specifications?: any;
    weatherConfig?: any;
    accessRequirements?: any;
    safetyRequirements?: any;
    manager?: {
      id: string;
      name: string;
      email?: string;
      phone?: string;
    };
    teams?: Array<{
      id: string;
      name: string;
      memberCount?: number;
    }>;
    [key: string]: any;
  };
  isDeleted: boolean;
  deletedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetConstructionSitesResponse {
  sites: ConstructionSite[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================================
// ROUTE PLANNING CONFIGURATION TYPES
// ============================================================================

export interface RoutePlanningConfiguration {
  enabled: boolean;
  defaultOptimizationParams: {
    prioritizeTime: boolean;
    prioritizeFuel: boolean;
    prioritizeCustomerPreference: boolean;
    maxRouteTime: number; // minutes
    maxStopsPerRoute: number;
    allowOvertime: boolean;
    considerTraffic: boolean;
    considerWeather: boolean;
    skillMatching: boolean;
    balanceWorkload: boolean;
  };
  integrations: {
    googleMaps: { 
      apiKey?: string; 
      enabled: boolean;
      geocodingEnabled: boolean;
      directionsEnabled: boolean;
      trafficEnabled: boolean;
    };
    weatherApi: { 
      enabled: boolean;
      considerInRouting: boolean;
      delayThresholds: {
        rain: number; // mm
        snow: number; // cm
        wind: number; // km/h
        temperature: { min: number; max: number }; // celsius
      };
    };
  };
  workingHours: { 
    start: string; // HH:MM
    end: string;   // HH:MM
    timezone: string;
    allowEarlyStart: boolean;
    allowLateFinish: boolean;
  };
  serviceRadius: number; // km - maximum distance from base
  allowOvertimeRoutes: boolean;
  maxDailyTasksPerTeam: number;
  automaticOptimization: {
    enabled: boolean;
    scheduleTime: string; // HH:MM - when to run daily optimization
    advanceDays: number; // how many days ahead to optimize
  };
  notifications: {
    routeAssigned: boolean;
    routeStarted: boolean;
    taskCompleted: boolean;
    delays: boolean;
    weatherAlerts: boolean;
  };
}

export interface UpdateRoutePlanningConfigRequest {
  enabled?: boolean;
  defaultOptimizationParams?: Partial<RoutePlanningConfiguration['defaultOptimizationParams']>;
  integrations?: {
    googleMaps?: Partial<RoutePlanningConfiguration['integrations']['googleMaps']>;
    weatherApi?: Partial<RoutePlanningConfiguration['integrations']['weatherApi']>;
  };
  workingHours?: Partial<RoutePlanningConfiguration['workingHours']>;
  serviceRadius?: number;
  allowOvertimeRoutes?: boolean;
  maxDailyTasksPerTeam?: number;
  automaticOptimization?: Partial<RoutePlanningConfiguration['automaticOptimization']>;
  notifications?: Partial<RoutePlanningConfiguration['notifications']>;
}

export interface UpdateGoogleMapsConfigRequest {
  apiKey?: string;
  enabled: boolean;
  geocodingEnabled?: boolean;
  directionsEnabled?: boolean;
  trafficEnabled?: boolean;
}

export interface UpdateWeatherConfigRequest {
  enabled: boolean;
  considerInRouting?: boolean;
  delayThresholds?: {
    rain?: number;
    snow?: number;
    wind?: number;
    temperature?: { min: number; max: number };
  };
}

export interface RoutePlanningConfigValidationResponse {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  integrationStatus: {
    googleMaps: { enabled: boolean; valid: boolean; errors?: string[] };
    weather: { enabled: boolean; valid: boolean; errors?: string[] };
  };
}

// Sync operation types
export interface SyncEmployeesResponse {
  success: boolean;
  message: string;
  syncedCount?: number;
  externalIdUpdates?: number;
  externalIdFailures?: number;
  logs: string[];
  summary?: any;
}

export interface SyncTasksResponse {
  success: boolean;
  message: string;
  syncedCount?: number;
  logs: string[];
  summary?: any;
}

export const businessGeneralApi = createApi({
  reducerPath: 'businessGeneralApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/general`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['StaffProfile', 'Department', 'Team', 'Role', 'RoutePlanningConfig', 'Projects', 'ConstructionSites', 'EmployeesWithoutQualityRoles'],
  endpoints: (builder) => ({
    // ============================================================================
    // STAFF PROFILE ENDPOINTS
    // ============================================================================
    
    // Get simple staff profile for modals/quick view
    getSimpleStaffProfile: builder.query<SimpleStaffProfileResponse, { staffId: string; businessId: string }>({
      query: ({ staffId, businessId }) => ({
        url: `/${staffId}/profile/simple`,
        params: { businessId }
      }),
       // @ts-ignore
      providesTags: (result, error, { staffId }) => [{ type: 'StaffProfile', id: staffId }]
    }),
    
    // Get full staff profile for detailed view
    getFullStaffProfile: builder.query<FullStaffProfileResponse, { staffId: string; businessId: string }>({
      query: ({ staffId, businessId }) => ({
        url: `/${staffId}/profile/full`,
        params: { businessId }
      }),
      // @ts-ignore
      providesTags: (result, error, { staffId }) => [{ type: 'StaffProfile', id: staffId }]
    }),

    // ============================================================================
    // DEPARTMENT MANAGEMENT ENDPOINTS
    // ============================================================================

    // Get all departments
    getDepartments: builder.query<{ departments: any[] }, { businessId: string }>({
      query: ({ businessId }) => ({
        url: '/departments',
        params: { businessId }
      }),
      providesTags: ['Department']
    }),

    // Create a new department
    createDepartment: builder.mutation<CreateDepartmentResponse, { businessId: string; departmentData: CreateDepartmentRequest }>({
      query: ({ businessId, departmentData }) => ({
        url: '/departments',
        method: 'POST',
        params: { businessId },
        body: departmentData
      }),
      invalidatesTags: ['Department']
    }),

    // Update an existing department
    updateDepartment: builder.mutation<UpdateDepartmentResponse, { 
      businessId: string; 
      departmentId: string; 
      updateData: UpdateDepartmentRequest 
    }>({
      query: ({ businessId, departmentId, updateData }) => ({
        url: `/departments/${departmentId}`,
        method: 'PUT',
        params: { businessId },
        body: updateData
      }),
      invalidatesTags: ['Department']
    }),

    // Remove a department
    removeDepartment: builder.mutation<RemoveDepartmentResponse, { businessId: string; departmentId: string }>({
      query: ({ businessId, departmentId }) => ({
        url: `/departments/${departmentId}`,
        method: 'DELETE',
        params: { businessId }
      }),
      invalidatesTags: ['Department']
    }),

    // Get all roles (both attached and custom)
    getRoles: builder.query<{ roles: any[] }, { businessId: string }>({
      query: ({ businessId }) => ({
        url: '/roles',
        params: { businessId }
      }),
      providesTags: ['Role']
    }),

    // Create a new role
    createRole: builder.mutation<CreateRoleResponse, { 
      businessId: string; 
      roleData: CreateRoleRequest 
    }>({
      query: ({ businessId, roleData }) => ({
        url: '/roles',
        method: 'POST',
        params: { businessId },
        body: roleData
      }),
      invalidatesTags: ['Role']
    }),

    // Update an existing role
    updateRole: builder.mutation<UpdateRoleResponse, { 
      businessId: string; 
      roleId: string; 
      updateData: UpdateRoleRequest 
    }>({
      query: ({ businessId, roleId, updateData }) => ({
        url: `/roles/${roleId}`,
        method: 'PUT',
        params: { businessId },
        body: updateData
      }),
      invalidatesTags: ['Role']
    }),

    // Remove a role
    removeRole: builder.mutation<RemoveRoleResponse, { 
      businessId: string; 
      roleId: string 
    }>({
      query: ({ businessId, roleId }) => ({
        url: `/roles/${roleId}`,
        method: 'DELETE',
        params: { businessId }
      }),
      invalidatesTags: ['Role']
    }),


    // ============================================================================
    // TEAM MANAGEMENT ENDPOINTS
    // ============================================================================

    // Get all teams (Enhanced for route planning)
    getTeams: builder.query<{ teams: any[] }, { businessId: string }>({
      query: ({ businessId }) => ({
        url: '/teams',
        params: { businessId }
      }),
      providesTags: ['Team']
    }),

    // Create a new team
    createTeam: builder.mutation<CreateTeamResponse, { businessId: string; teamData: CreateTeamRequest }>({
      query: ({ businessId, teamData }) => ({
        url: '/teams',
        method: 'POST',
        params: { businessId },
        body: teamData
      }),
      invalidatesTags: ['Team']
    }),

    // Update an existing team
    updateTeam: builder.mutation<UpdateTeamResponse, { 
      businessId: string; 
      teamId: string; 
      updateData: UpdateTeamRequest 
    }>({
      query: ({ businessId, teamId, updateData }) => ({
        url: `/teams/${teamId}`,
        method: 'PUT',
        params: { businessId },
        body: updateData
      }),
      invalidatesTags: ['Team']
    }),

    // Remove a team
    removeTeam: builder.mutation<RemoveTeamResponse, { businessId: string; teamId: string }>({
      query: ({ businessId, teamId }) => ({
        url: `/teams/${teamId}`,
        method: 'DELETE',
        params: { businessId }
      }),
      invalidatesTags: ['Team']
    }),

    // Get single team with stats and activity
    getTeam: builder.query({
      query: ({ businessId, teamId }) => ({
        url: `/teams/${teamId}`,
        params: { businessId }
      }),
      // @ts-ignore
      providesTags: (result, error, { teamId }) => [
        { type: 'SingleTeam', id: teamId },
        'Teams'
      ]
    }),

    // Update field team (comprehensive)
    updateFieldTeam: builder.mutation({
      query: ({ teamId, businessId, ...updateData }) => ({
        url: `/teams/${teamId}/field-update`,
        method: 'PUT',
        params: { businessId },
        body: updateData
      }),
      // @ts-ignore
      invalidatesTags: (result, error, { teamId }) => [
        { type: 'SingleTeam', id: teamId },
        'Teams',
        'TeamLocations',
        'LocationStats'
      ]
    }),

    // ============================================================================
    // EMPLOYEE QUALITY ROLES ENDPOINTS
    // ============================================================================

    // Get employees without quality roles - NEW ENDPOINT
    getEmployeesWithoutQualityRoles: builder.query<EmployeeWithoutQualityRoleResponse, { businessId: string }>({
      query: ({ businessId }) => ({
        url: '/employees/quality-roles/available',
        params: { businessId }
      }),
      providesTags: ['EmployeesWithoutQualityRoles']
    }),

    // ============================================================================
    // PROJECTS ENDPOINTS (NEW)
    // ============================================================================

    // Get all projects
    getProjects: builder.query<GetProjectsResponse, { 
      businessId: string; 
      page?: number; 
      limit?: number; 
      status?: string;
      projectType?: string;
    }>({
      query: ({ businessId, page = 1, limit = 50, status, projectType }) => ({
        url: '/projects',
        params: { 
          businessId, 
          page, 
          limit,
          ...(status && { status }),
          ...(projectType && { projectType })
        }
      }),
      providesTags: ['Projects']
    }),

    // Get single project
    getProject: builder.query<{ project: AppProject }, { businessId: string; projectId: string }>({
      query: ({ businessId, projectId }) => ({
        url: `/projects/${projectId}`,
        params: { businessId }
      }),
      // @ts-ignore
      providesTags: (result, error, { projectId }) => [{ type: 'Projects', id: projectId }]
    }),

    // Create new project
    createProject: builder.mutation<{ success: boolean; projectId: string; message: string }, {
      businessId: string;
      projectData: {
        name: string;
        description?: string;
        clientId?: string;
        status?: string;
        metadata?: any;
      };
    }>({
      query: ({ businessId, projectData }) => ({
        url: '/projects',
        method: 'POST',
        params: { businessId },
        body: projectData
      }),
      invalidatesTags: ['Projects']
    }),

    // ============================================================================
    // CONSTRUCTION SITES ENDPOINTS (NEW)
    // ============================================================================

    // Get all construction sites
    getConstructionSites: builder.query<GetConstructionSitesResponse, { 
      businessId: string; 
      page?: number; 
      limit?: number; 
      status?: string;
      type?: string;
      projectId?: string;
    }>({
      query: ({ businessId, page = 1, limit = 50, status, type, projectId }) => ({
        url: '/construction-sites',
        params: { 
          businessId, 
          page, 
          limit,
          ...(status && { status }),
          ...(type && { type }),
          ...(projectId && { projectId })
        }
      }),
      providesTags: ['ConstructionSites']
    }),

    // Get single construction site
    getConstructionSite: builder.query<{ site: ConstructionSite }, { businessId: string; siteId: string }>({
      query: ({ businessId, siteId }) => ({
        url: `/construction-sites/${siteId}`,
        params: { businessId }
      }),
      // @ts-ignore
      providesTags: (result, error, { siteId }) => [{ type: 'ConstructionSites', id: siteId }]
    }),

    // Create new construction site
    createConstructionSite: builder.mutation<{ success: boolean; siteId: string; message: string }, {
      businessId: string;
      siteData: {
        name: string;
        description?: string;
        appProjectId?: string;
        status?: string;
        type?: string;
        location?: any;
        metadata?: any;
      };
    }>({
      query: ({ businessId, siteData }) => ({
        url: '/construction-sites',
        method: 'POST',
        params: { businessId },
        body: siteData
      }),
      invalidatesTags: ['ConstructionSites']
    }),

    // ============================================================================
    // ROUTE PLANNING CONFIGURATION ENDPOINTS
    // ============================================================================

    // Get route planning configuration
    getRoutePlanningConfig: builder.query<{ config: RoutePlanningConfiguration | null }, { businessId: string }>({
      query: ({ businessId }) => ({
        url: '/route-planning/config',
        params: { businessId }
      }),
      providesTags: ['RoutePlanningConfig']
    }),

    // Update route planning configuration
    updateRoutePlanningConfig: builder.mutation<
      { success: boolean; message: string }, 
      { businessId: string; configData: UpdateRoutePlanningConfigRequest }
    >({
      query: ({ businessId, configData }) => ({
        url: '/route-planning/config',
        method: 'PUT',
        params: { businessId },
        body: configData
      }),
      invalidatesTags: ['RoutePlanningConfig']
    }),

    // Reset route planning configuration to defaults
    resetRoutePlanningConfig: builder.mutation<
      { success: boolean; message: string; config: RoutePlanningConfiguration }, 
      { businessId: string }
    >({
      query: ({ businessId }) => ({
        url: '/route-planning/config/reset',
        method: 'POST',
        params: { businessId }
      }),
      invalidatesTags: ['RoutePlanningConfig']
    }),

    // Update Google Maps integration settings
    updateGoogleMapsConfig: builder.mutation<
      { success: boolean; message: string; isValid?: boolean; errors?: string[] }, 
      { businessId: string; googleMapsConfig: UpdateGoogleMapsConfigRequest }
    >({
      query: ({ businessId, googleMapsConfig }) => ({
        url: '/route-planning/config/integrations/google-maps',
        method: 'PUT',
        params: { businessId },
        body: googleMapsConfig
      }),
      invalidatesTags: ['RoutePlanningConfig']
    }),

    // Update weather integration settings
    updateWeatherConfig: builder.mutation<
      { success: boolean; message: string }, 
      { businessId: string; weatherConfig: UpdateWeatherConfigRequest }
    >({
      query: ({ businessId, weatherConfig }) => ({
        url: '/route-planning/config/integrations/weather',
        method: 'PUT',
        params: { businessId },
        body: weatherConfig
      }),
      invalidatesTags: ['RoutePlanningConfig']
    }),

    // Validate route planning configuration
    validateRoutePlanningConfig: builder.mutation<
      RoutePlanningConfigValidationResponse, 
      { businessId: string }
    >({
      query: ({ businessId }) => ({
        url: '/route-planning/config/validate',
        method: 'POST',
        params: { businessId }
      })
    }),

    // ============================================================================
    // SYNC OPERATION ENDPOINTS
    // ============================================================================

    // Sync employees from VenueBoost
    syncEmployees: builder.mutation<SyncEmployeesResponse, { businessId: string }>({
      query: ({ businessId }) => ({
        url: '/sync/employees',
        method: 'POST',
        params: { businessId }
      }),
      invalidatesTags: ['StaffProfile', 'EmployeesWithoutQualityRoles']
    }),

    // Update department skills
    updateDepartmentSkills: builder.mutation<
      { success: boolean; message: string }, 
      { 
        businessId: string; 
        departmentId: string; 
        skillsData: {
          requiredSkills?: string[];
          optionalSkills?: string[];
          skillWeights?: Record<string, number>;
        }
      }
    >({
      query: ({ businessId, departmentId, skillsData }) => ({
        url: `/departments/${departmentId}/skills`,
        method: 'PUT',
        params: { businessId },
        body: skillsData
      }),
      invalidatesTags: ['Department']
    }),

    // Get department skills
    getDepartmentSkills: builder.query<
      {
        department: any;
        skillRequirements: {
          required: string[];
          optional: string[];
          weights: Record<string, number>;
        };
      }, 
      { businessId: string; departmentId: string }
    >({
      query: ({ businessId, departmentId }) => ({
        url: `/departments/${departmentId}/skills`,
        params: { businessId }
      }),
      providesTags: ['Department']
    }),

    // Sync department skills
    syncDepartmentSkills: builder.mutation<
      { success: boolean; message: string; syncedDepartments: number }, 
      { businessId: string }
    >({
      query: ({ businessId }) => ({
        url: '/departments/sync-skills',
        method: 'POST',
        params: { businessId }
      }),
      invalidatesTags: ['Department']
    }),

    // Sync tasks from VenueBoost
    syncTasks: builder.mutation<SyncTasksResponse, { businessId: string }>({
      query: ({ businessId }) => ({
        url: '/sync/tasks',
        method: 'POST',
        params: { businessId }
      })
    })
  })
});

export const {
  // Staff Profile hooks
  useGetSimpleStaffProfileQuery,
  useGetFullStaffProfileQuery,
  
  // Department Management hooks
  useGetDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useRemoveDepartmentMutation,
  
  // Team Management hooks
  useGetTeamsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useRemoveTeamMutation,
  useGetTeamQuery,
  useUpdateFieldTeamMutation,

  // Employee Quality Roles hooks (NEW)
  useGetEmployeesWithoutQualityRolesQuery,

  // Projects hooks (NEW)
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,

  // Construction Sites hooks (NEW)
  useGetConstructionSitesQuery,
  useGetConstructionSiteQuery,
  useCreateConstructionSiteMutation,
  
  // Route Planning Configuration hooks
  useGetRoutePlanningConfigQuery,
  useUpdateRoutePlanningConfigMutation,
  useResetRoutePlanningConfigMutation,
  useUpdateGoogleMapsConfigMutation,
  useUpdateWeatherConfigMutation,
  useValidateRoutePlanningConfigMutation,
  
  // Sync Operation hooks
  useSyncEmployeesMutation,
  useSyncTasksMutation,
  useUpdateDepartmentSkillsMutation,
  useGetDepartmentSkillsQuery,
  useSyncDepartmentSkillsMutation,

  // Role Management hooks (NEW)
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useRemoveRoleMutation,

} = businessGeneralApi;